import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Heart } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NavItem = ({ to, label, testid, onClick }) => (
    <NavLink
        to={to}
        end={to === "/"}
        onClick={onClick}
        data-testid={testid}
        className={({ isActive }) =>
            `relative font-sans text-sm tracking-wide font-medium px-3 py-2 transition-colors ${
                isActive ? "text-caireen-orange" : "text-caireen-ink/80 hover:text-caireen-orange"
            }`
        }
    >
        {({ isActive }) => (
            <>
                <span>{label}</span>
                {isActive && (
                    <motion.span
                        layoutId="nav-underline"
                        className="absolute -bottom-0.5 left-2 right-2 h-[2px] bg-caireen-orange rounded-full"
                    />
                )}
            </>
        )}
    </NavLink>
);

export default function Header() {
    const { t, lang, setLang, supportedLanguages, translations } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    const items = [
        { to: "/", label: t.nav.home, testid: "nav-home" },
        { to: "/about", label: t.nav.about, testid: "nav-about" },
        { to: "/projects", label: t.nav.projects, testid: "nav-projects" },
        { to: "/news", label: t.nav.news, testid: "nav-news" },
        { to: "/donate", label: t.nav.donate, testid: "nav-donate" },
        { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-caireen-cream/85 backdrop-blur-xl border-b border-caireen-ink/10 shadow-[0_4px_24px_-12px_rgba(31,41,55,0.15)]"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between px-5 md:px-8 py-3 md:py-4">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
                    <span className="relative w-11 h-11 md:w-12 md:h-12 rounded-full overflow-hidden ring-2 ring-caireen-orange/20 transition-transform group-hover:rotate-6">
                        <img src="/caireen-logo.jpg" alt="Caireen Verein" className="w-full h-full object-cover" />
                    </span>
                    <span className="hidden sm:flex flex-col leading-tight">
                        <span className="font-serif text-lg md:text-xl font-semibold text-caireen-ink">Caireen</span>
                        <span className="font-sans text-[10px] tracking-[0.18em] uppercase text-caireen-ink/60">Verein</span>
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-1">
                    {items.map((i) => (
                        <NavItem key={i.to} {...i} />
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                data-testid="lang-switcher"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-sans font-medium text-caireen-ink/80 hover:bg-caireen-ink/5 transition-colors"
                            >
                                <Globe className="w-4 h-4" strokeWidth={1.75} />
                                <span className="font-semibold">{translations[lang].meta.code}</span>
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="font-sans">
                            {supportedLanguages.map((code) => (
                                <DropdownMenuItem
                                    key={code}
                                    data-testid={`lang-option-${code}`}
                                    onClick={() => setLang(code)}
                                    className={lang === code ? "font-semibold text-caireen-orange" : ""}
                                >
                                    <span className="mr-2 text-xs tracking-widest">{translations[code].meta.code}</span>
                                    {translations[code].meta.lang}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <Link to="/donate" className="hidden md:inline-flex">
                        <Button
                            data-testid="header-donate-cta"
                            className="rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold px-5 shadow-[0_8px_24px_-12px_rgba(232,133,51,0.7)] transition-all"
                        >
                            <Heart className="w-4 h-4 mr-1.5" strokeWidth={2} />
                            {t.nav.donateCta}
                        </Button>
                    </Link>

                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="lg:hidden p-2 rounded-full hover:bg-caireen-ink/5 transition-colors"
                        aria-label="Menu"
                    >
                        {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.2 }}
                        className="lg:hidden bg-caireen-cream/95 backdrop-blur-xl border-t border-caireen-ink/10"
                        data-testid="mobile-menu"
                    >
                        <div className="max-w-7xl mx-auto px-5 py-3 flex flex-col gap-1">
                            {items.map((i) => (
                                <NavLink
                                    key={i.to}
                                    to={i.to}
                                    end={i.to === "/"}
                                    data-testid={`mobile-${i.testid}`}
                                    onClick={() => setOpen(false)}
                                    className={({ isActive }) =>
                                        `px-3 py-3 rounded-xl font-sans font-medium ${
                                            isActive
                                                ? "bg-caireen-orange/10 text-caireen-orange"
                                                : "text-caireen-ink/80 hover:bg-caireen-ink/5"
                                        }`
                                    }
                                >
                                    {i.label}
                                </NavLink>
                            ))}
                            <Link to="/donate" onClick={() => setOpen(false)}>
                                <Button
                                    data-testid="mobile-donate-cta"
                                    className="w-full mt-2 rounded-full bg-caireen-orange hover:bg-caireen-red text-white font-semibold"
                                >
                                    <Heart className="w-4 h-4 mr-1.5" />
                                    {t.nav.donateCta}
                                </Button>
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
